import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { HttpStatusCode } from "axios";
import { useSetAtom } from "jotai";
import { useTranslation } from "react-i18next";
import { ErrorResponse } from "../datasources/api";
import { sendDelete, sendGet, sendPost, sendPostPdf, sendPut } from "../datasources/networking/resources";
import { ResourceUri, uriHasResourceId } from "../datasources/requests/common";
import { useCurrentLanguage } from "../i18n";
import { makeSnackbarMessage, publishSnackbarMessage } from "../state/view";
import { invalidateCache, reloadFn } from "./common";
import { useAuthentication } from "./useAuthentication";

type ActionKey = 'post' | 'put' | 'delete' | 'download';

type AfterMutationOptions<T> = {
	actionKey: ActionKey;
	onSuccess?: (response?: T) => void;
	onFailure?: (error?: ErrorResponse) => void;
	// onSuccessMessage?: string;
	// onFailureMessage?: string;
};

type ReadResourceOptions<T> = {
	emptyFallback?: T;
	requireResourceId?: boolean;
	readMethod?: "get" | "post";
	disabled?: boolean;
};

type WriteResourceOptions<T> = {
	defaultPayload?: Partial<T>;
	subjectKey?: string;
	overwriteErrorMessage?: boolean;
};

export const useResource = <Req extends object, Res>(
	uri: ResourceUri,
	readOptions?: ReadResourceOptions<Res>,
	writeOptions?: WriteResourceOptions<Req>,
	payload?: Req
) => {
	const [data, setData] = useState<Res | undefined>(readOptions?.emptyFallback);

	const {
		data: rData,
		isLoading: rIsLoading,
		error: rError,
		reload: rReload,
	} = useReadResource<Req, Res>(uri, readOptions, payload);

	const {
		data: wData,
		isLoading: wIsLoading,
		error: wError,
		post,
		put,
		del,
		clearErrors,
		postPdf,
		postCsv,
	} = useWriteResource<Req, Res>(uri, writeOptions);

	useEffect(() => {
		setData(wData ?? rData);
	}, [rData, wData]);

	const reload = () => {
		rReload();
		clearErrors();
	};

	return {
		data,
		isLoading: wIsLoading || rIsLoading,
		error: wError ?? rError,
		reload,
		post,
		put,
		del,
		postPdf,
		postCsv,
	};
};

export const useReadResource = <Req, Res>(uri: ResourceUri, options?: ReadResourceOptions<Res>, payload?: Req) => {
	const { forceSignIn } = useAuthentication();

	const client = useQueryClient();

	const language = useCurrentLanguage();

	const queryKey = [
		uri.route,
		uri.resourceId,
		uri.query,
		uri.path,
		options?.readMethod,
		options?.requireResourceId,
		options?.emptyFallback,
		language,
		payload,
	];

	const disabled =
		(!options?.emptyFallback && options?.requireResourceId && !uriHasResourceId(uri)) || options?.disabled;

	const { data, isLoading, isFetching, error } = useQuery<Res | undefined, ErrorResponse>({
		queryKey,
		queryFn: async () => {
			if (options?.emptyFallback && options.requireResourceId && !uriHasResourceId(uri)) {
				return options.emptyFallback;
			}

			return !options?.readMethod || options.readMethod === "get"
				? await sendGet<Res>(uri)
				: await sendPost<Res, Req>(payload as Req, uri);
		},
		staleTime: 10 * 60 * 1000,
		enabled: !disabled,
	});

	const reload = () => {
		reloadFn(client, queryKey).then();
	};

	const clearCache = () => {
		invalidateCache(client, queryKey).then();
	};

	if (error && error.httpCode === HttpStatusCode.Unauthorized) {
		forceSignIn(error);
	}

	return {
		data,
		isLoading: isLoading || isFetching,
		error,
		reload, clearCache,
	};
};

export const useWriteResource = <Req extends object, Res>(uri: ResourceUri, writeOptions?: WriteResourceOptions<Req>) => {
	const { t } = useTranslation(["common", "snackbar"]);
	const { forceSignIn } = useAuthentication();
	const language = useCurrentLanguage();
	const publishMessage = useSetAtom(publishSnackbarMessage);
	const [data, setData] = useState<Res | undefined>(undefined);

	const mergePayload = (payload: Req): Req =>
		({
			...writeOptions?.defaultPayload,
			...payload,
		}) as Req;

	const _post = useMutation<Res, ErrorResponse, Req>({
		mutationKey: [uri, "post", language, writeOptions?.defaultPayload],
		mutationFn: async (payload) => {
			const response = await sendPost<Res, Req>(mergePayload(payload), uri);
			setData(response);

			return response;
		},
	});

	const _put = useMutation<Res, ErrorResponse, Req>({
		mutationKey: [uri, "put", language, writeOptions?.defaultPayload],
		mutationFn: async (payload) => {
			const response = await sendPut<Res, Req>(mergePayload(payload), uri);
			setData(response);

			return response;
		},
	});

	const _delete = useMutation<Res, ErrorResponse>({
		mutationKey: [uri, "delete", language],
		mutationFn: async () => {
			const response = await sendDelete<Res>(uri);
			setData(response);

			return response;
		},
	});

	const _postPdf = useMutation<Blob, ErrorResponse, Req>({
		mutationKey: [uri, "postPdf", language, writeOptions?.defaultPayload],
		mutationFn: async (payload) => {
			return await sendPostPdf(mergePayload(payload), uri);
		},
	});

	const clearErrors = () => {
		_post.reset();
		_put.reset();
		_delete.reset();
		_postPdf.reset();
	};

	const performOnSuccess = (options: AfterMutationOptions<Res>, response?: Res) => {
		setData(response);
		options.onSuccess?.(response);
		if (writeOptions?.subjectKey && writeOptions?.subjectKey !== "") {
			publishMessage(
				makeSnackbarMessage(
					t(`snackbar:success/${options.actionKey}`, { subj: t(`snackbar:subject/${writeOptions.subjectKey}`) }),
					"success"
				)
			);
		}
	};

	const performOnFailure = (options: AfterMutationOptions<Res>, error?: ErrorResponse) => {
		options.onFailure?.(error);
		if (writeOptions?.subjectKey && writeOptions?.subjectKey !== "" && writeOptions?.overwriteErrorMessage) {
			publishMessage(
				makeSnackbarMessage(
					t(`snackbar:failure/${options.actionKey}`, { subj: t(`snackbar:subject/${writeOptions.subjectKey}`) }),
					"error"
				)
			);
		} else if (error && error.message && error.message !== "") {
			publishMessage(makeSnackbarMessage(error.message, "error"));
		} else {
			publishMessage(makeSnackbarMessage(t("snackbar:failure/generic"), "error"));
		}
	};

	const post = (payload: Req, options?: Partial<AfterMutationOptions<Res>>): Promise<Res> =>
		_post.mutateAsync(payload, {
			onSuccess: (response) => performOnSuccess({...options, actionKey: options?.actionKey || 'post'}, response),
			onError: (error) => performOnFailure({...options, actionKey: options?.actionKey || 'post'}, error),
		});

	const put = (payload: Req, options?: Partial<AfterMutationOptions<Res>>) =>
		_put.mutateAsync(payload, {
			onSuccess: (response) => performOnSuccess({...options, actionKey: options?.actionKey || 'put'}, response),
			onError: (error) => performOnFailure({...options, actionKey: options?.actionKey || 'put'}, error),
		});

	const del = (options?: Partial<AfterMutationOptions<Res>>) =>
		_delete.mutateAsync(undefined, {
			onSuccess: (response) => performOnSuccess({...options, actionKey: options?.actionKey || 'delete'}, response),
			onError: (error) => performOnFailure({...options, actionKey: options?.actionKey || 'delete'}, error),
		});

	const error = _post.error ?? _put.error ?? _delete.error ?? _postPdf.error;

	if (error && error.httpCode === HttpStatusCode.Unauthorized) {
		forceSignIn(error);
	}

	const postPdf = async (payload: Req, filename: string) => {
		try {
			const arrayBuffer = await _postPdf.mutateAsync(payload);

			const blob = new Blob([arrayBuffer], {
				type: "application/pdf",
			});
			const url = URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = filename;
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			URL.revokeObjectURL(url);

			performOnSuccess({ actionKey: 'download' });
		} catch (err: any) {
			performOnFailure({ actionKey: 'download' });
		}
	};

	const postCsv = async (payload: Req, filename: string) => {
		try {
			const arrayBuffer = await _postPdf.mutateAsync(payload);
			const blob = new Blob([arrayBuffer], {
				type: "text/csv;charset=utf-8;",
			});
			const url = URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = filename;
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			URL.revokeObjectURL(url);

			performOnSuccess({ actionKey: 'download' });
		} catch (err: any) {
			performOnFailure({ actionKey: 'download' });
		}
	};

	return {
		data,
		isLoading: _post.isPending || _put.isPending || _delete.isPending || _postPdf.isPending,
		error,
		post,
		put,
		del,
		clearErrors,
		postPdf,
		postCsv,
	};
};
